/**
 * BatchInputSelectors - Input selectors for unified batch mode
 *
 * Template, Collection, Prompt, and AspectRatio selectors.
 */

import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Template {
  id: string;
  name: string;
}

interface Collection {
  id: string;
  name: string;
  max_images: number;
}

interface TemplateSelectorProps {
  templateId: string;
  templates: Template[];
  onChange: (id: string) => void;
  disabled: boolean;
}

export function TemplateSelector({ templateId, templates, onChange, disabled }: TemplateSelectorProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{t('batch.labelTemplate')}</label>
      <select
        value={templateId}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
      >
        <option value="">{t('batch.templateChoose')}</option>
        {templates.map((t) => (
          <option key={t.id} value={t.id}>{t.name}</option>
        ))}
      </select>
      {templates.length === 0 && (
        <p className="text-sm text-neutral-500 flex items-center gap-1">
          <Info className="h-4 w-4" />
          {t('batch.noTemplatesHint')}
        </p>
      )}
    </div>
  );
}

interface CollectionSelectorProps {
  collectionId: string;
  collections: Collection[];
  collectionImageCount: number;
  onChange: (id: string) => void;
  disabled: boolean;
}

export function CollectionSelector({
  collectionId,
  collections,
  collectionImageCount,
  onChange,
  disabled,
}: CollectionSelectorProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
        {t('batch.labelCollection')}
      </label>
      <select
        value={collectionId}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
      >
        <option value="">{t('batch.collectionChoose')}</option>
        {collections.map((c) => (
          <option key={c.id} value={c.id}>{c.name} {t('batch.collectionMaxImages', { count: c.max_images })}</option>
        ))}
      </select>
      {collections.length === 0 && (
        <p className="text-sm text-neutral-500 flex items-center gap-1">
          <Info className="h-4 w-4" />
          {t('batch.noCollectionsHint')}
        </p>
      )}
      {collectionImageCount > 0 && (
        <p className="text-sm text-neutral-500">
          {t('batch.collectionPerInput', { count: collectionImageCount })}
        </p>
      )}
    </div>
  );
}

interface PromptInputProps {
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}

export function PromptInput({ value, onChange, disabled }: PromptInputProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{t('batch.labelPrompt')}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('batch.transformPromptPlaceholder')}
        disabled={disabled}
        rows={3}
        className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 resize-none"
      />
    </div>
  );
}

interface AspectRatioSelectorProps {
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}

export function AspectRatioSelector({ value, onChange, disabled }: AspectRatioSelectorProps) {
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Seitenverhaltnis</h4>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
      >
        <option value="1:1">1:1 (Quadratisch)</option>
        <option value="16:9">16:9 (Breit)</option>
        <option value="9:16">9:16 (Hochformat)</option>
        <option value="4:3">4:3</option>
        <option value="3:2">3:2</option>
      </select>
    </div>
  );
}
